import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Search, MoreVertical, Pencil, Trash2 } from "lucide-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CreateAudienceModal from "../Ads/CreateAudienceModal";
import "../../components/Toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Audiences = () => {
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState([
    {
      id: "business",
      title: "Business Professionals",
      description: "Entrepreneurs, executives, and business owners",
      icon: "Briefcase",
      demographics: "25-55 years • Professional • B2B focused",
      createdAt: "2024-01-15",
      usageCount: 12,
      detailedDescription:
        "This audience consists of ambitious business professionals including entrepreneurs, C-level executives, and business owners who are focused on growth, efficiency, and professional development. They value time-saving solutions and are willing to invest in quality products and services that help them achieve their business goals.",
      interests: [
        "Business Strategy",
        "Leadership",
        "Productivity",
        "Networking",
        "Innovation",
        "Financial Growth",
      ],
      painPoints: [
        "Time Management",
        "Scaling Operations",
        "Team Management",
        "Market Competition",
        "Work-Life Balance",
      ],
      preferredChannels: [
        "LinkedIn",
        "Email",
        "Industry Publications",
        "Webinars",
        "Professional Events",
      ],
      buyingBehavior:
        "Research-driven, values ROI, prefers premium solutions, influenced by case studies and testimonials",
      contentPreferences: [
        "Case Studies",
        "Industry Reports",
        "How-to Guides",
        "Expert Interviews",
        "Data-driven Content",
      ],
    },
    {
      id: "students",
      title: "Students & Young Adults",
      description: "College students and recent graduates",
      icon: "GraduationCap",
      demographics: "18-28 years • Education-focused • Tech-savvy",
      createdAt: "2024-01-10",
      usageCount: 8,
      detailedDescription:
        "Tech-savvy students and young adults who are digital natives, budget-conscious, and focused on education, career development, and social connections. They're early adopters of new technologies and heavily influenced by peer recommendations and social media.",
      interests: [
        "Technology",
        "Career Development",
        "Social Media",
        "Gaming",
        "Music",
        "Travel",
        "Fitness",
      ],
      painPoints: [
        "Student Debt",
        "Job Market Competition",
        "Budget Constraints",
        "Academic Pressure",
        "Career Uncertainty",
      ],
      preferredChannels: [
        "Instagram",
        "TikTok",
        "YouTube",
        "Discord",
        "Twitter",
        "Snapchat",
      ],
      buyingBehavior:
        "Price-sensitive, influenced by peers, prefers online shopping, values convenience and speed",
      contentPreferences: [
        "Video Content",
        "Memes",
        "Tutorials",
        "Behind-the-scenes",
        "User-generated Content",
      ],
    },
    {
      id: "parents",
      title: "Parents & Families",
      description: "Parents with children of various ages",
      icon: "Heart",
      demographics: "28-45 years • Family-oriented • Value-conscious",
      createdAt: "2024-01-08",
      usageCount: 15,
      detailedDescription:
        "Dedicated parents who prioritize their family's well-being, safety, and development. They're value-conscious shoppers who research thoroughly before making purchases, especially for products that affect their children. They seek solutions that save time and make parenting easier.",
      interests: [
        "Child Development",
        "Education",
        "Health & Wellness",
        "Family Activities",
        "Home Organization",
        "Cooking",
      ],
      painPoints: [
        "Time Management",
        "Child Safety",
        "Educational Choices",
        "Work-Life Balance",
        "Budget Management",
      ],
      preferredChannels: [
        "Facebook",
        "Pinterest",
        "Parenting Blogs",
        "Email",
        "YouTube",
        "Local Community Groups",
      ],
      buyingBehavior:
        "Safety-focused, values reviews and recommendations, prefers trusted brands, considers long-term value",
      contentPreferences: [
        "Parenting Tips",
        "Product Reviews",
        "Family Activities",
        "Educational Content",
        "Safety Guides",
      ],
    },
    {
      id: "gamers",
      title: "Gamers & Tech Enthusiasts",
      description: "Gaming community and technology lovers",
      icon: "Gamepad2",
      demographics: "16-35 years • Tech-savvy • Entertainment-focused",
      createdAt: "2024-01-05",
      usageCount: 6,
      detailedDescription:
        "Passionate gamers and technology enthusiasts who stay up-to-date with the latest gaming trends, hardware, and tech innovations. They're active in online communities, value performance and quality, and are willing to invest in premium gaming equipment and experiences.",
      interests: [
        "Gaming",
        "Technology",
        "Streaming",
        "Esports",
        "Hardware",
        "Software",
        "Virtual Reality",
      ],
      painPoints: [
        "Hardware Costs",
        "Game Performance",
        "Online Competition",
        "Time Management",
        "Staying Current with Trends",
      ],
      preferredChannels: [
        "Twitch",
        "YouTube",
        "Discord",
        "Reddit",
        "Gaming Forums",
        "Steam",
        "Twitter",
      ],
      buyingBehavior:
        "Performance-focused, influenced by reviews and benchmarks, early adopters, values community opinions",
      contentPreferences: [
        "Game Reviews",
        "Tech Specs",
        "Tutorials",
        "Live Streams",
        "Community Content",
      ],
    },
    {
      id: "creatives",
      title: "Creatives & Artists",
      description: "Designers, artists, and creative professionals",
      icon: "Palette",
      demographics: "22-40 years • Creative • Visual-oriented",
      createdAt: "2024-01-03",
      usageCount: 9,
      detailedDescription:
        "Creative professionals including graphic designers, artists, photographers, and content creators who value aesthetics, originality, and self-expression. They're visual learners who appreciate quality design and are always looking for inspiration and tools to enhance their creative work.",
      interests: [
        "Design",
        "Art",
        "Photography",
        "Creative Tools",
        "Inspiration",
        "Freelancing",
        "Portfolio Building",
      ],
      painPoints: [
        "Creative Block",
        "Client Management",
        "Pricing Work",
        "Finding Inspiration",
        "Tool Costs",
        "Market Saturation",
      ],
      preferredChannels: [
        "Instagram",
        "Behance",
        "Dribbble",
        "Pinterest",
        "YouTube",
        "Creative Blogs",
        "LinkedIn",
      ],
      buyingBehavior:
        "Quality-focused, values aesthetics, influenced by visual content, prefers creative and unique solutions",
      contentPreferences: [
        "Visual Content",
        "Tutorials",
        "Inspiration Galleries",
        "Behind-the-scenes",
        "Tool Reviews",
      ],
    },
    {
      id: "lifestyle",
      title: "Lifestyle & Wellness",
      description: "Health, fitness, and lifestyle enthusiasts",
      icon: "Utensils",
      demographics: "25-50 years • Health-conscious • Active lifestyle",
      createdAt: "2024-01-01",
      usageCount: 11,
      detailedDescription:
        "Health-conscious individuals who prioritize wellness, fitness, and maintaining an active lifestyle. They're interested in sustainable living, mental health, nutrition, and personal development. They value authenticity and are willing to invest in products and services that support their wellness goals.",
      interests: [
        "Fitness",
        "Nutrition",
        "Mental Health",
        "Sustainable Living",
        "Personal Development",
        "Mindfulness",
      ],
      painPoints: [
        "Time for Self-care",
        "Information Overload",
        "Maintaining Motivation",
        "Cost of Healthy Living",
        "Work-Life Balance",
      ],
      preferredChannels: [
        "Instagram",
        "YouTube",
        "Health Blogs",
        "Fitness Apps",
        "Pinterest",
        "Wellness Communities",
      ],
      buyingBehavior:
        "Values authenticity, influenced by testimonials, prefers natural/organic products, research-driven",
      contentPreferences: [
        "Wellness Tips",
        "Workout Videos",
        "Healthy Recipes",
        "Success Stories",
        "Educational Content",
      ],
    },
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAudience, setEditingAudience] = useState(null);
  const [, setLoading] = useState(true);
  const [, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [updateAudience, ] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const fetchAudiences = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}audiences`,
          {
            headers: {
              Authorization: `Bearer ${token}`, // Include the token in the request header
            },
          }
        );
        setIsLoading(false);
        setAudiences(response.data.data);
      } catch (err) {
        setError("Failed to load brands. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAudiences();
  }, [showCreateModal, updateAudience]);

  const filteredAudiences = audiences.filter(
    (audience) =>
      audience.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audience.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const truncateDescription = (description) => {
    if (!description) return "";

    const words = description.trim().split(/\s+/);
    return words.length > 15 ? `${words.slice(0, 14).join(" ")}...` : description;
  };

  const handleSaveAudience = (audienceData) => {
    // if (editingAudience) {
    //   // Update existing audience
    //   setAudiences((prev) =>
    //     prev.map((audience) =>
    //       audience.id === editingAudience.id ? { ...audienceData, id: editingAudience.id } : audience
    //     )
    //   );
    //   setEditingAudience(null);
    // } else {
    //   // Create new audience
    //   setAudiences((prev) => [
    //     ...prev,
    //     { ...audienceData, createdAt: new Date().toISOString().split("T")[0], usageCount: 0 },
    //   ]);
    // }
    setShowCreateModal(false);
  };



  const handleEditAudience = (audience) => {
    const audiencee = {
      id: audience._id,
      title: audience.name,
      description: audience.description,
      icon: "Utensils",
      demographics: audience.ageRange + " years • " + audience.educationLevel,
      createdAt: audience.createdAt,
      details: {
        ageRange: audience.ageRange,
        location: audience.location,
        income: audience.incomeLevel,
        education: audience.educationLevel,
        interests: audience.interests,
        pinPointsAndChallenges: audience.pinPointsAndChallenges,
        preferredChannels: audience.preferredChannels,
        contentPreferences: audience.contentPreferences,
        // painPoints : response.data.data.pinPointsAndChallenges,
        goals: audience.goalsAndMotivations,
      },
      usageCount: 11,
      detailedDescription: audience.description,
      interests: audience.interests,
      pinPointsAndChallenges: audience.pinPointsAndChallenges,
      preferredChannels: audience.preferredChannels,
      buyingBehavior:
        "Values authenticity, influenced by testimonials, prefers natural/organic products, research-driven",
      contentPreferences: audience.contentPreferences,
    };
    setEditingAudience(audiencee);
    setShowCreateModal(true);
  };









  const handleArchiveAudience = (audience) => {
    // Archive audience (soft delete)
    // This could move to an archived state instead of deleting
    setAudiences((prev) => prev.filter((a) => a.id !== audience.id));
  };

  const handleAudienceClick = (audienceId) => {
    navigate(`/app/audiences/${audienceId}`);
  };

  const handleDropdownToggle = (e, audienceId) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === audienceId ? null : audienceId);
  };

  useEffect(() => {
    const handleClick = () => setOpenDropdownId(null);
    if (openDropdownId !== null) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [openDropdownId]);

  const getIconComponent = (iconName) => {
    const iconMap = {
      Briefcase: (
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0v2a2 2 0 01-2 2H10a2 2 0 01-2-2V6"
          />
        </svg>
      ),
      GraduationCap: (
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 14l9-5-9-5-9 5 9 5z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
          />
        </svg>
      ),
      Heart: (
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
      Gamepad2: (
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
          />
        </svg>
      ),
      Palette: (
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"
          />
        </svg>
      ),
      Utensils: (
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01"
          />
        </svg>
      ),
    };
    return iconMap[iconName] || iconMap.Briefcase;
  };

  return (
    <>
      {isLoading ? (
        <div className="App-loading">
          <div className="App-logo" title="Zimzee" />
        </div>
      ) : (
        <div className="responsive-page responsive-page--audiences min-h-screen bg-white dark:bg-[#0A0A0A]">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-[#fff]">
                  My Audiences
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Manage your target audience profiles
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingAudience(null);
                  setShowCreateModal(true);
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add New Audience
              </button>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search audiences..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                />
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredAudiences.length} audience
                {filteredAudiences.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Audiences Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAudiences.map((audience) => (
                <div
                  key={audience._id}
                  className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleAudienceClick(audience._id)}
                >
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          {getIconComponent("Briefcase")}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-[#fff]">
                            {audience.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {truncateDescription(audience.description)}
                          </p>
                        </div>
                      </div>
                      <div className="relative">
                        <button
                          className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 rounded flex items-center justify-center"
                          onClick={(e) =>
                            handleDropdownToggle(e, audience.id || audience._id)
                          }
                        >
                          <MoreVertical className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </button>
                        {openDropdownId === (audience.id || audience._id) && (
                          <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
                            <button
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditAudience(audience);
                                setOpenDropdownId(null);
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchiveAudience(
                                  audience.id || audience._id
                                );
                                setOpenDropdownId(null);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Demographics */}
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                        {audience.ageRange} years
                      </span>
                      {Array.isArray(audience.pinPointsAndChallenges)
                        ? audience.pinPointsAndChallenges.map((tag, index) => (
                          <span
                            key={`${tag}-${index}`}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                          >
                            {tag}
                          </span>
                        ))
                        : audience.pinPointsAndChallenges && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                            {audience.pinPointsAndChallenges}
                          </span>
                        )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <span>
                        Created{" "}
                        {moment(audience?.createdAt).format("MMM D, YYYY")}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredAudiences.length === 0 && searchTerm && (
              <div className="text-center py-12">
                <svg
                  className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-[#fff] mb-2">
                  No audiences found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Try adjusting your search terms
                </p>
              </div>
            )}

            {/* Empty State - No audiences */}
            {audiences.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-[#fff] mb-2">
                  No audiences yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create your first audience to get started
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Create New Audience
                </button>
              </div>
            )}
          </div>

          {/* Create/Edit Audience Modal */}
          <CreateAudienceModal
            open={showCreateModal}
            onOpenChange={setShowCreateModal}
            onSave={handleSaveAudience}
            editingAudience={editingAudience}
          />
          <ToastContainer />
        </div>
      )}
    </>
  );
};

export default Audiences;
